import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Building2, FileText, Search, Truck, Users } from "lucide-react";
import { Kunde360View } from "@/components/Kunde360View";
import { useOrgCurrency } from "@/lib/currency-context";

const API = process.env.REACT_APP_API || "";
function LieferantDetail({ supplierId, onBack }) {
  const { formatMoney } = useOrgCurrency();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const headers = useCallback(() => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  }, []);

  useEffect(() => {
    (async () => {
      try { const result = await axios.get(`${API}/api/360/lieferant/${supplierId}`, { headers: headers() }); setData(result.data); }
      catch (error) { console.error(error); }
      finally { setLoading(false); }
    })();
  }, [supplierId, headers]);

  if (loading) return <p className="text-sm text-gray-400">Lade Lieferantenprofil…</p>;
  if (!data) return <p className="text-sm text-gray-400">Lieferant nicht gefunden.</p>;
  const { lieferant, zusammenfassung: s } = data;

  return <div className="space-y-4">
    <div className="flex items-center gap-3"><Button variant="ghost" size="sm" onClick={onBack}><ArrowLeft className="w-4 h-4" /></Button><div><h2 className="text-xl font-bold">{lieferant.firmenname}</h2><p className="text-sm text-gray-500">{lieferant.ansprechpartner || ""} {lieferant.email ? `| ${lieferant.email}` : ""}</p></div></div>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <Card className="p-3"><div className="text-xs text-gray-500">Offene Beträge</div><div className="text-lg font-bold text-red-600">{formatMoney(s.offene_betraege)}</div></Card>
      <Card className="p-3"><div className="text-xs text-gray-500">Bezahlt gesamt</div><div className="text-lg font-bold text-green-600">{formatMoney(s.bezahlt_gesamt)}</div></Card>
      <Card className="p-3"><div className="text-xs text-gray-500">Ausgaben gesamt</div><div className="text-lg font-bold">{formatMoney(s.ausgaben_gesamt)}</div></Card>
      <Card className="p-3"><div className="text-xs text-gray-500">Verträge</div><div className="text-lg font-bold">{s.anzahl_vertraege}</div></Card>
    </div>
    <Card className="p-4"><h3 className="font-semibold mb-3">Historie</h3><div className="space-y-2">{data.timeline?.length ? data.timeline.map((entry, index) => <div key={index} className="flex gap-3 text-sm border-l-2 border-gray-200 pl-3"><span className="text-xs text-gray-400 w-20 shrink-0">{entry.datum}</span><span>{entry.titel}</span><span className="text-xs text-gray-400 ml-auto">{entry.studio}</span></div>) : <p className="text-sm text-gray-400">Noch keine Historie vorhanden.</p>}</div></Card>
  </div>;
}

export function Profile360Hub() {
  const [mode, setMode] = useState("kunden");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const headers = useCallback(() => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  }, []);

  useEffect(() => { setQuery(""); setResults([]); setSelectedId(""); }, [mode]);
  useEffect(() => {
    if (query.length < 2) { setResults([]); return; }
    const timer = setTimeout(async () => {
      try {
        const path = mode === "kunden" ? "/api/360/kunden" : "/api/360/lieferanten";
        const response = await axios.get(path.startsWith("http") ? path : `${API}${path}`, { headers: headers(), params: { q: query } });
        setResults(response.data || []);
      } catch (error) { console.error(error); }
    }, 250);
    return () => clearTimeout(timer);
  }, [query, mode, headers]);

  if (selectedId && mode === "kunden") return <Kunde360View kundeId={selectedId} onBack={() => setSelectedId("")} />;
  if (selectedId && mode === "lieferanten") return <LieferantDetail supplierId={selectedId} onBack={() => setSelectedId("")} />;

  const isCustomer = mode === "kunden";
  return <div className="space-y-4">
    <div><h2 className="text-xl font-bold">{isCustomer ? "Kunden-360°" : "Lieferanten-360°"}</h2><p className="text-sm text-gray-500">Alle berechtigten Informationen aus den Studios an einem Ort.</p></div>
    <div className="flex gap-2"><Button variant={isCustomer ? "default" : "outline"} onClick={() => setMode("kunden")}><Users className="w-4 h-4 mr-1" />Kunden</Button><Button variant={!isCustomer ? "default" : "outline"} onClick={() => setMode("lieferanten")}><Truck className="w-4 h-4 mr-1" />Lieferanten</Button></div>
    <Card className="p-4"><div className="flex items-center gap-2"><Search className="w-4 h-4 text-gray-400" /><Input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={isCustomer ? "Kunde, Firma oder E-Mail suchen…" : "Lieferant oder Ansprechpartner suchen…"} autoFocus /></div>{query.length >= 2 && <div className="mt-3 divide-y">{results.length ? results.map((row) => <button key={row.id} onClick={() => setSelectedId(row.id)} className="w-full flex justify-between text-left py-2.5 hover:bg-gray-50"><div><div className="font-medium text-sm">{isCustomer ? (row.kunde_name || row.kunde_firma) : row.firmenname}</div><div className="text-xs text-gray-500">{isCustomer ? row.kunde_email : (row.ansprechpartner || row.email)}</div></div><span className="text-xs text-[var(--brand-primary)]">360° öffnen</span></button>) : <p className="text-sm text-gray-400 py-3">Keine passenden Einträge.</p>}</div>}</Card>
  </div>;
}
